import { FC } from 'react'
import { Surgery } from 'common/types'
import EventStatus from './EventStatus'
import {
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material'
import { useProcedureListTableLogic } from './ProcedureListTable.logic'
import './ProcedureListTable.scss'
import { ProcedureListTableProps } from './ProcedureList.types'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { CustomModal } from 'components/molecules/Modal/Modal'
import Notifications from 'views/Notifications/Notifications'
import BellIcon from 'assets/icons/BellIcon'
import specialRequestIcon from 'assets/icons/images/specialRequestIcon.png'
import successIcon from 'assets/icons/images/success.png'
import AddIcon from '@mui/icons-material/Add'
import { SpecialRequestModal } from './SpecialRequestModal/SpecialRequestModal'
import { SpecialRequestInfoModal } from './SpecialRequestInfoModal/SpecialRequestInfoModal'
import EditProcedureModal from './EditProcedureModal/EditProcedureModal'
import CancelProcedureModal from './CancelProcedureModal/CancelProcedureModal'
import CaseInformation from './CaseInformation'
import { PatientStatusModal } from './PatientStatusModal/PatientStatusModal'
import { shouldAllowPatientStatusRestrictedActions } from 'lib/utils/patientStatusUtils'
import { ProcedureOptionsMenu } from './ProcedureOptionsMenu'
import { AssignCardModal } from 'views/PreferenceCards/AssignCardModal/AssignCardModal'

export const ProcedureListTable: FC<ProcedureListTableProps> = (props) => {
  const { loading, beforeDate, afterDate, refetch } = props

  const logic = useProcedureListTableLogic(props)

  let testIndex = 0

  // ignores '<a> appearing as a child of <tbody>' warning in console to allow link component in table row
  console.error = (message) => {
    if (
      typeof message === 'string' &&
      message.startsWith('Warning: validateDOMNesting')
    ) {
      return
    }
  }

  const SpecialRequestIcon: FC<{
    success?: boolean
    qty?: number
    surgeryId: string
  }> = ({ success = false, qty = 0 }) => (
    <div
      className="special-request-icon-wrapper"
      style={{ position: 'relative' }}
    >
      <img
        src={specialRequestIcon}
        alt="special-request"
        className="special-request-icon"
      />
      {qty > 0 ? (
        <div className="special-request-qty-wrapper">
          <Typography variant="caption" fontWeight={800} fontSize={10}>
            {qty}
          </Typography>
        </div>
      ) : (
        <div className="add-special-request-wrapper">
          <AddIcon className="add-special-request" />
        </div>
      )}
      {success && <img src={successIcon} alt="success" className="success" />}
    </div>
  )

  return (
    <TableContainer className="table-container">
      <Table>
        <TableBody>
          {loading ? (
            <TableRow className="table-row-initial-state">
              <div className="table-cell-loading">
                <CircularProgress />
              </div>
            </TableRow>
          ) : !logic.hasItems ? (
            <TableRow className="table-row-initial-state">
              <TableCell className="table-cell-empty">
                <Typography variant="subtitle2">
                  There are no procedures currently scheduled that match your
                  filter criteria
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            logic.sortedSurgeries?.map((surgery: Surgery) => {
              if (surgery?.isTestProcedure) {
                testIndex += 1
              }

              return (
                <TableRow
                  key={surgery?._id}
                  className="table-row"
                  data-testid={
                    surgery?.isTestProcedure
                      ? `test-procedure-${testIndex}`
                      : 'table-row'
                  }
                  component={'div'}
                  onClick={() => {
                    if (surgery.status?.name === 'CANCELLED') {
                      return
                    }

                    if (
                      !logic.flags.enablePatientStatus ||
                      shouldAllowPatientStatusRestrictedActions(
                        logic.allowPatientStatusRestrictedActions,
                        surgery?.patientStatusHistory?.[0]?.status
                      )
                    ) {
                      logic.handleStartSurgery(surgery)
                    }
                  }}
                >
                  <TableCell
                    className={`table-cell-case-info ${
                      logic.isMobile ? 'is-mobile' : ''
                    }`}
                  >
                    {surgery && (
                      <CaseInformation
                        surgery={surgery}
                        showDate={logic.showDate}
                        onClick={() =>
                          logic.setShowPatientStatusModal({
                            show: true,
                            surgery,
                          })
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell
                    className="table-cell-event-status"
                    data-testid="event-status"
                  >
                    {logic.isRep ||
                    surgery.status.name === 'CANCELLED' ? null : (
                      <div className="icons-wrapper">
                        <ProcedureOptionsMenu
                          surgery={surgery}
                          onEditProcedure={(surgery) =>
                            logic.setEditModalSurgery(surgery)
                          }
                          onCancelProcedure={(surgery) =>
                            logic.setCancelModalSurgery(surgery)
                          }
                          onAssignPreferenceCard={(surgery) => {
                            logic.setIsAssignCardModalOpen({
                              isOpen: true,
                              surgery,
                            })
                          }}
                          onUnassignPreferenceCard={(surgery) => {
                            logic.handleUnassignPreference(surgery)
                          }}
                        />
                        {logic.isRep ? null : logic.flags
                            .caseHomeNotificationsButton ? (
                          <IconButton
                            className="bell-icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (!logic.hasNotifications(surgery)) return
                              logic.setIsNotificationsOpen({
                                isOpen: true,
                                surgery,
                              })
                            }}
                          >
                            {logic.hasNotifications(surgery) ? (
                              <BellIcon />
                            ) : (
                              <NotificationsIcon color="disabled" />
                            )}
                          </IconButton>
                        ) : null}

                        {logic.flags.enableSpecialRequest &&
                          !logic.isRep &&
                          (surgery.status.name === 'SCANNING' ||
                            (surgery.status.name === 'SUBMITTED' &&
                              surgery.specialRequests &&
                              surgery.specialRequests.length > 0)) && (
                            <>
                              {
                                <IconButton
                                  id={
                                    surgery.specialRequests &&
                                    surgery.specialRequests.length > 0
                                      ? 'showSpecialRequests'
                                      : 'addSpecialRequest'
                                  }
                                  onClick={(e) =>
                                    logic.handleSpecialRequest(
                                      e,
                                      surgery._id,
                                      surgery.status.name
                                    )
                                  }
                                >
                                  <div className="special-request-icon-wrapper">
                                    <SpecialRequestIcon
                                      qty={
                                        surgery.specialRequests
                                          ? surgery.specialRequests.length
                                          : 0
                                      }
                                      surgeryId={surgery._id}
                                    />
                                  </div>
                                </IconButton>
                              }
                            </>
                          )}
                      </div>
                    )}
                    <EventStatus
                      isStarted={surgery.isStarted}
                      isTestProcedure={surgery?.isTestProcedure}
                      isScans={surgery?.assetGroups.length !== 0}
                      surgeryStatus={surgery?.status.name}
                      hasUndocumentedScans={surgery?.undocumentedScansCount > 0}
                      implantReportStatus={surgery?.implantReportStatus}
                      addendumStatus={surgery?.addendums?.length > 0}
                      patientStatusHistory={surgery?.patientStatusHistory}
                    />
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>

      <CustomModal
        fitContentWidth={logic.isMobile}
        open={logic.isNotificationsOpen.isOpen}
        header="Notifications"
        isLoadingState={false}
        secondaryButtonAction={() =>
          logic.setIsNotificationsOpen({ isOpen: false })
        }
        primaryButtonDisabled
        closeButtonOnly
      >
        <Notifications
          surgery={logic.isNotificationsOpen?.surgery as Surgery}
          isMobile={logic.isMobile}
        />
      </CustomModal>

      {logic.showSpecialRequestModal.showModal === 'add' && (
        <SpecialRequestModal
          surgeryId={logic.showSpecialRequestModal.surgeryId as string}
          onClose={logic.handleCloseSpecialRequest}
          beforeDate={beforeDate}
          afterDate={afterDate}
        />
      )}

      {logic.loadingSpecialRequests && (
        <div className="loader">
          <CircularProgress />
        </div>
      )}

      {logic.showSpecialRequestModal.showModal === 'show' && (
        <SpecialRequestInfoModal
          canAdd={logic.showSpecialRequestModal.surgeryStatus === 'SCANNING'}
          showModal={!logic.loadingSpecialRequests}
          surgeryId={logic.showSpecialRequestModal.surgeryId as string}
          onClose={logic.handleCloseSpecialRequest}
          onHeaderButtonAction={(surgeryId) => {
            logic.handleCloseSpecialRequest()
            logic.setShowSpecialRequestModal((prev) => ({
              ...prev,
              showModal: 'add',
              surgeryId,
            }))
          }}
          onFinishLoading={logic.setLoadingSpecialRequests}
        />
      )}

      {logic.editModalSurgery && (
        <EditProcedureModal
          open={!!logic.editModalSurgery}
          onClose={logic.handleCloseEditModal}
          surgery={logic.editModalSurgery}
          beforeDate={beforeDate}
          afterDate={afterDate}
        />
      )}

      {logic.cancelModalSurgery && (
        <CancelProcedureModal
          open={!!logic.cancelModalSurgery}
          onClose={logic.handleCloseCancelModal}
          surgery={logic.cancelModalSurgery}
          refetch={refetch}
        />
      )}

      {logic.showPatientStatusModal.show &&
        logic.showPatientStatusModal.surgery && (
          <PatientStatusModal
            surgery={logic.showPatientStatusModal.surgery}
            surgeries={logic.sortedSurgeries}
            onClose={logic.setShowPatientStatusModal}
            refetch={refetch}
          />
        )}

      {logic.isAssignCardModalOpen && (
        <AssignCardModal
          open={logic.isAssignCardModalOpen.isOpen}
          onClose={() =>
            logic.setIsAssignCardModalOpen({
              isOpen: false,
              surgery: null,
            })
          }
          onCreateNew={logic.handleCreatePreferenceCard}
          surgery={logic.isAssignCardModalOpen.surgery}
          refetch={refetch}
        />
      )}
    </TableContainer>
  )
}
